"use client";

import { useSelector } from "react-redux";
import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES } from "@/contants";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";

import DreamnMatchOdds from "./DreamnMatchOdds";
import DreamnMatchTeam from "./DreamnMatchTeam";
import DreamnMatchTime from "./DreamnMatchTime";
import DreamnMatchMinute from "./DreamnMatchMinute";
import DreamnOtherBetsDropdown from "./DreamnOtherBetsDropdown";
import { getDreamnOddsForPeriod } from "./dreamnOddsPeriod";

dayjs.extend(utc);
dayjs.extend(timezone);

const MATCH_STATUS_FINISHED = 8;

const FONT_BODY = "text-xs md:text-sm";

export default function DreamnMatch({ match, isLast, index }) {
  const [selectedPeriod, setSelectedPeriod] = useState("HT");
  const [openOtherOdds, setOpenOtherOdds] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);

  const { getMatchOdds } = useMatches();
  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const { hdc: showHdc, ou: showOu, x2: showX2 } = useSelector((state) => state.toggleReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const { currentHomeScore, currentAwayScore } = useMatchData(liveScore, match);

  const oddsRaw = useMatchOdds(match, filterOdds, companyIdOdds);

  const oddsData = getDreamnOddsForPeriod(oddsRaw, selectedPeriod === "HT");

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isMatchFinished = match?.status === MATCH_STATUS_FINISHED;

  const TZ = "Asia/Ho_Chi_Minh";
  const matchTimeStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("HH:mm")
      : "--";

  let statusLabel;
  if (isLive) {
    statusLabel = (
      <DreamnMatchMinute
        liveScore={liveScore}
        status={statusNum}
        matchTime={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        className="font-bold"
      />
    );
  } else if (isMatchFinished) {
    statusLabel = "Kết thúc";
  } else {
    statusLabel = "Sắp đá";
  }

  const htScore =
    match?.homeHalfScore !== undefined && match?.awayHalfScore !== undefined
      ? `${match.homeHalfScore}-${match.awayHalfScore}`
      : liveScore?.homeHtScore !== undefined && liveScore?.awayHtScore !== undefined
        ? `${liveScore.homeHtScore}-${liveScore.awayHtScore}`
        : "";

  const handleTogglePeriod = (e) => {
    e.stopPropagation();
    setSelectedPeriod((prev) => (prev === "FT" ? "HT" : "FT"));
  };

  const handleOtherOddsClick = (e) => {
    if (countOtherOdds <= 1) return;
    e.stopPropagation();
    e.preventDefault();
    setOpenOtherOdds((prev) => !prev);
    if (!openOtherOdds && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
    : [];
  const correctScoresListHalf = otherOddsData?.correctScoresHalfOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds)
    : [];
  const correctScoresFull = splitCorrectScoresListToSections(correctScoresListFull);
  const correctScoresHalf = splitCorrectScoresListToSections(correctScoresListHalf);
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds)
    ? otherOddsData.totalCornersOdds
    : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds)
    ? otherOddsData.totalCornersHalfOdds
    : [];
  const goalOddEvenFirst = goalOddEvenOdds?.[0] ?? {};
  const countOtherOdds = Number(match?.countOtherOdds ?? 0);
  const showOtherOddsButton = countOtherOdds > 1;
  const hasOtherData =
    goalOddEvenOdds?.length > 0 ||
    correctScoresListFull.length > 0 ||
    totalCornersOdds.length > 0 ||
    correctScoresListHalf.length > 0 ||
    totalCornersHalfOdds.length > 0;

  return (
    <div
      className={` flex flex-col border-t-0 border-l-0 border-r-0 md:mb-0 ${!isLast
        ? "border-b border-dashed kbv-row-separator-navy-slate"
        : "border-b-0"
        } kbv-row-hover-navy-slate md:p-2 py-3 px-2 md:py-4 transition-all cursor-pointer gap-5 md:gap-2 ${index % 2 === 0 ? "bg-[var(--theme-row-even)]" : "bg-[var(--theme-row-odd)]"
        }`}
    >
      <DreamnMatchOdds
        {...oddsData}
        showHdc={showHdc}
        showOu={showOu}
        showX2={showX2}
        periodLabel={selectedPeriod}
        onTogglePeriod={handleTogglePeriod}
      />

      <DreamnMatchTeam
        homeName={match?.homeName}
        awayName={match?.awayName}
        homeLogo={match?.homeTeam?.logo}
        awayLogo={match?.awayTeam?.logo}
        score={
          isLive || isMatchFinished
            ? `${currentHomeScore} - ${currentAwayScore}`
            : "vs"
        }
        isLive={isLive}
      />

      <div className="flex w-full flex-col gap-1">
        <DreamnMatchTime
          time={matchTimeStr}
          statusLabel={statusLabel}
          openOtherOdds={openOtherOdds}
          onOtherOddsClick={handleOtherOddsClick}
          showOtherOddsButton={showOtherOddsButton}
          halfTimeScore={htScore}
        />
        {showOtherOddsButton && openOtherOdds && (
          <DreamnOtherBetsDropdown
            loadingOtherOdds={loadingOtherOdds}
            otherOddsData={otherOddsData}
            hasOtherData={hasOtherData}
            isLive={isLive}
            goalOddEvenOdds={goalOddEvenOdds}
            goalOddEvenFirst={goalOddEvenFirst}
            correctScoresListFull={correctScoresListFull}
            correctScoresFull={correctScoresFull}
            correctScoresListHalf={correctScoresListHalf}
            correctScoresHalf={correctScoresHalf}
            totalCornersOdds={totalCornersOdds}
            totalCornersHalfOdds={totalCornersHalfOdds}
            fontBody={FONT_BODY}
          />
        )}
      </div>
    </div>
  );
}
